import { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Tag } from '@digdir/designsystemet-react';
import type { QuestionTrend } from '../../../api/queries';
import { BarChart } from '../../../components/BarChart';
import { LineChart } from '../../../components/LineChart';
import { DateUtils } from '../../../utils/DateUtils';
import { formatScore } from '../../../components/AnalysisLayout/analysis.utils';
import {
  changeTones,
  colorForScore,
  getLatestScore,
  getPeriodChange,
  parseCalendarDate,
  toChartData,
} from '../questionTrends.utils';
import classes from '../QuestionTrends.module.css';

export type ChartType = 'bar' | 'line';

const arrows = { up: '↗', down: '↘', stable: '—' } as const;

type QuestionTrendCardProps = {
  questionTrend: QuestionTrend;
  chartType: ChartType;
  size: 'small' | 'large';
  headingId: string;
  headingRef?: Ref<HTMLHeadingElement>;
  /** Tekst over spørsmålet, for eksempel «Spørsmål 2 av 7» */
  eyebrow?: string;
  footer?: ReactNode;
};

/** Ett spørsmål: siste snitt, endring i perioden og grafen */
export const QuestionTrendCard = ({
  questionTrend,
  chartType,
  size,
  headingId,
  headingRef,
  eyebrow,
  footer,
}: QuestionTrendCardProps) => {
  const { t } = useTranslation();
  const latest = getLatestScore(questionTrend);
  const change = getPeriodChange(questionTrend);
  const data = toChartData(questionTrend);
  const chartHeight = size === 'large' ? 300 : 200;
  const description = questionTrend.trends
    .map((trend) => `${DateUtils.getShortDateFromDate(parseCalendarDate(trend.date))} ${formatScore(trend.score, 2)}`)
    .join(', ');

  return (
    <>
      {eyebrow && <span className={classes.counter}>{eyebrow}</span>}
      <div className={classes.cardHeader}>
        <Heading
          id={headingId}
          ref={headingRef}
          tabIndex={headingRef ? -1 : undefined}
          level={2}
          data-size={size === 'large' ? 'sm' : '2xs'}
          className={classes.question}
        >
          {questionTrend.question.question}
        </Heading>
        <div className={classes.cardScore}>
          {latest !== undefined && (
            <span className={classes.latest}>
              <span className={classes.srOnly}>{t('questionTrends.page.latest')} </span>
              {formatScore(latest, 2)}
            </span>
          )}
          <Tag data-size="sm" data-color={change ? changeTones[change.direction] : 'neutral'} className={classes.tag}>
            {change ? (
              <>
                <span aria-hidden="true">{arrows[change.direction]} </span>
                {t(`questionTrends.page.change.${change.direction}`, {
                  value: formatScore(Math.abs(change.diff), 2),
                })}
              </>
            ) : (
              t('questionTrends.page.oneSurvey')
            )}
          </Tag>
        </div>
      </div>
      <div role="img" aria-label={`${questionTrend.question.question}: ${description}`} className={classes.chart}>
        {chartType === 'bar' ? (
          <BarChart data={data} variant="compact" height={chartHeight} colorForScore={colorForScore} />
        ) : (
          <LineChart data={data} variant="compact" height={chartHeight} colorForScore={colorForScore} />
        )}
      </div>
      {footer}
    </>
  );
};
